/**
 * The review session's client-side state (brief §flashcards: "again-cards
 * come back in-session"). Pure data helpers, persisted to `sessionStorage`
 * like `practice/session.ts` so a refresh survives; the UI lives in
 * `ReviewPage.tsx`.
 *
 * Each card carries its own `previews` (from the queue fetch, refreshed from
 * the review response after an "again") so the four rating buttons can show
 * "what happens if I press this" before the student rates — never
 * recomputed client-side; always the server's `shared/sm2` numbers.
 */
import type { Rating } from "@shared/sm2";
import type { PreviewDto, QueueCardDto, QueueItemDto } from "./types";

const STORAGE_KEY = "mf.flashcards.session";

export interface SessionCard extends QueueCardDto {
  previews: PreviewDto[];
}

export interface ReviewSessionData {
  startedAt: number;
  chapter?: number;
  topic?: string;
  /** The mixed review across all moments (a queue with neither chapter nor topic). */
  mixed?: boolean;
  /** Where the summary's "Tillbaka till dagens pass" goes; already passed through `safeReturnTo`. */
  returnTo?: string;
  cardsById: Record<string, SessionCard>;
  /** Pending card ids; the first ready entry is the one on screen. */
  order: string[];
  /** In-session eligibility; absent entries (including old saves) are ready now. */
  readyAtByCardId?: Record<string, number>;
  reviewed: number;
  again: number;
  /** Earliest `dueAt` among cards that left the session (i.e. not "again"). */
  nextDueAt: number | null;
}

export function createReviewSession(
  items: readonly QueueItemDto[],
  filter: { chapter?: number; topic?: string } = {},
  options: { mixed?: boolean; returnTo?: string } = {},
): ReviewSessionData {
  const cardsById: Record<string, SessionCard> = {};
  for (const item of items) cardsById[item.card.id] = { ...item.card, previews: item.previews };
  return {
    startedAt: Date.now(),
    ...(filter.chapter !== undefined ? { chapter: filter.chapter } : {}),
    ...(filter.topic ? { topic: filter.topic } : {}),
    ...(options.mixed ? { mixed: true } : {}),
    ...(options.returnTo ? { returnTo: options.returnTo } : {}),
    cardsById,
    order: items.map((item) => item.card.id),
    reviewed: 0,
    again: 0,
    nextDueAt: null,
  };
}

export function currentCard(session: ReviewSessionData, nowMs = Date.now()): SessionCard | null {
  const id = session.order.find((id) => (session.readyAtByCardId?.[id] ?? 0) <= nowMs);
  return id !== undefined ? (session.cardsById[id] ?? null) : null;
}

/** Earliest stored eligibility among pending cards, or null when none is stored. */
export function nextReadyAt(session: ReviewSessionData): number | null {
  const times = session.order.flatMap((id) => {
    const readyAt = session.readyAtByCardId?.[id];
    return readyAt === undefined ? [] : [readyAt];
  });
  return times.length ? Math.min(...times) : null;
}

/**
 * Applies one rating for the card currently on screen. "again" sends it to
 * the back of this session's queue with its refreshed previews and the server
 * delay (spec §4.6: re-queued after 10 min); every other rating removes it and may
 * lower `nextDueAt`.
 */
export function applyReview(
  session: ReviewSessionData,
  cardId: string,
  rating: Rating,
  nextDueAt: number,
  previews: PreviewDto[],
  requeueInMinutes = 10,
  nowMs = Date.now(),
): ReviewSessionData {
  const rest = session.order.filter((id) => id !== cardId);
  const isAgain = rating === "again";
  const order = isAgain ? [...rest, cardId] : rest;
  const readyAtByCardId = { ...session.readyAtByCardId };
  if (isAgain) readyAtByCardId[cardId] = nowMs + requeueInMinutes * 60_000;
  else delete readyAtByCardId[cardId];
  const existing = session.cardsById[cardId];
  const cardsById =
    isAgain && existing
      ? { ...session.cardsById, [cardId]: { ...existing, previews } }
      : session.cardsById;
  return {
    ...session,
    order,
    readyAtByCardId,
    cardsById,
    reviewed: session.reviewed + 1,
    again: session.again + (isAgain ? 1 : 0),
    nextDueAt: isAgain
      ? session.nextDueAt
      : session.nextDueAt === null
        ? nextDueAt
        : Math.min(session.nextDueAt, nextDueAt),
  };
}

export function isReviewComplete(session: ReviewSessionData): boolean {
  return session.order.length === 0;
}

/** Cards that have left the session out of all of them; an "again" card is still pending. */
export function sessionProgress(session: ReviewSessionData): { done: number; total: number } {
  const total = Object.keys(session.cardsById).length;
  return { done: Math.max(0, total - session.order.length), total };
}

/** The cards after the one on screen, in queue order, for the "Nästa kort" panel. */
export function upcomingCards(session: ReviewSessionData, currentId: string, limit = 5) {
  return session.order
    .filter((id) => id !== currentId)
    .slice(0, limit)
    .flatMap((id) => {
      const card = session.cardsById[id];
      return card ? [card] : [];
    });
}

/** Share of reviews rated "again" — 0 for a session with no reviews yet. */
export function againRate(session: ReviewSessionData): number {
  return session.reviewed === 0 ? 0 : session.again / session.reviewed;
}

export function saveSession(data: ReviewSessionData): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* storage full or unavailable (private mode) — the session still works in memory */
  }
}

export function loadSession(): ReviewSessionData | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ReviewSessionData;
  } catch {
    return null;
  }
}

export function clearSession(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
